/**
 * Logique PURE du traitement de la voix ENTRANTE (le correspondant) : bornes du
 * volume haut-parleur et paramètres de la chaîne « clarté ». Séparée du WebAudio
 * pour être testable unitairement (le câblage des nœuds est dans le
 * SipController, qui consomme ces constantes/fonctions).
 *
 * La voix d'un appelant RTC/PSTN arrive en bande étroite (G.711, 300–3400 Hz) :
 * on ne peut pas « ajouter » de la bande passante. Ce qu'on PEUT faire pour la
 * rendre plus intelligible sans la dénaturer :
 *   - un passe-haut doux qui retire le ronflement/grondement sous la voix ;
 *   - un compresseur LÉGER qui égalise les correspondants trop forts/trop
 *     faibles (on n'a plus à remonter/baisser le volume à chaque appel) ;
 *   - un gain de sortie réglable, avec possibilité de BOOST au-delà de 100 %
 *     (impossible via `<audio>.volume` qui plafonne à 1).
 *
 * Le traitement reste OPT-IN (désactivé par défaut) : volume 100 % + clarté
 * coupée ⇒ lecture directe via l'élément `<audio>`, strictement identique au
 * comportement de production actuel (aucune régression par défaut).
 */

/** Volume haut-parleur : 0 = muet, 1 = normal, 2 = ×2 (boost). */
export const SPEAKER_MIN = 0
export const SPEAKER_MAX = 2

/** Borne le ratio de volume haut-parleur dans [0, 2] (NaN/négatif → défaut 1). */
export function clampSpeakerVolume(ratio: number): number {
  if (!Number.isFinite(ratio)) return 1
  return Math.max(SPEAKER_MIN, Math.min(SPEAKER_MAX, ratio))
}

/** Au-delà de 1, `<audio>.volume` ne suffit plus (plafonné à 1) : il faut le
 *  graphe WebAudio (GainNode). Vrai aussi dès que la clarté est active. */
export function needsWebAudio(ratio: number, clarityOn: boolean): boolean {
  return clarityOn || clampSpeakerVolume(ratio) > 1
}

/** Paramètres de la chaîne de clarté entrante (réglages doux et sûrs : on
 *  améliore l'intelligibilité sans « pomper » ni dénaturer la voix). */
export const CLARITY = {
  /** Passe-haut : coupe le grondement/ronflement sous la voix utile. */
  highpassHz: 120,
  /** Compresseur léger (Web Audio DynamicsCompressorNode). */
  compressor: {
    threshold: -28, // dB : ne comprime que les pics
    knee: 24,       // dB : transition douce
    ratio: 2.5,     // léger (pas un limiteur)
    attack: 0.006,  // s
    release: 0.18,  // s
  },
} as const
